import React from "react";
import { FaEnvelope, FaInstagram, FaChurch, FaArrowUp } from "react-icons/fa";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, staggerChildren: 0.2 } },
  };

  const itemFadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <footer className="relative bg-gray-900 text-white pt-20 pb-8 overflow-hidden">
      {/* Effetto onda superiore */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-16" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                fill="white"></path>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* CTA Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-sacra-primary to-sacra-accent rounded-3xl p-8 sm:p-12 mb-16 shadow-2xl"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Unisciti al Pellegrinaggio
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Contattaci per informazioni o per partecipare come volontario
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="mailto:pellegrinaggiosacrasanmichele@gmail.com"
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-full"
              >
                <FaEnvelope /> Inviaci un'email
              </motion.a>
              <Link to="/Iscrizione"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-bold rounded-full hover:bg-black transition-colors">
                Iscriviti ora
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Grid Footer */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center md:text-left border-t border-gray-800 pt-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          {/* Colonna 1: Logo/Nome */}
          <motion.div variants={itemFadeIn}>
            <h3 className="text-2xl font-bold mb-4 flex items-center justify-center md:justify-start text-sacra-accent">
              <FaChurch className="mr-3" />
              Pellegrinaggio Sacra San Michele
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              Il cammino storico dalla città di Avigliana all'Abbazia millenaria della Valle di Susa. Un'esperienza di fede e natura, ideato e organizzato dall'Opera San Michele di Torino.
            </p>
          </motion.div>

          {/* Colonna 2: Social */}
          <motion.div variants={itemFadeIn} className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4 text-white uppercase tracking-wider text-sm">Resta Connesso</h4>
            <div className="space-y-3">
              <a href="https://www.instagram.com/pellegrinaggiosanmichele/" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-gray-400 hover:text-sacra-accent transition-colors duration-300">
                <FaInstagram className="text-xl" />
                <span>Instagram</span>
              </a>
            </div>
          </motion.div>

          {/* Colonna 3: Note Legali */}
          <motion.div variants={itemFadeIn} className="flex flex-col items-center md:items-start">
            <h4 className="text-lg font-semibold mb-4 text-white uppercase tracking-wider text-sm">Note Legali</h4>
            <div className="flex flex-col space-y-3">
              <Link to="/Privacy" className="text-gray-400 hover:text-sacra-accent transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/Cookie" className="text-gray-400 hover:text-sacra-accent transition-colors duration-300">
                Cookie Policy
              </Link>
            </div>
          </motion.div>
        </motion.div>

        {/* Copyright */}
        <div className="mt-12 text-center text-sm text-gray-500 pb-4 flex flex-col sm:flex-row justify-center items-center gap-1 sm:gap-2">
          <span>© 2025 – {new Date().getFullYear()} Pellegrinaggio Sacra di San Michele. Tutti i diritti riservati.</span>
          <span>
            Creato con <span className="text-red-500 mx-1">♥</span> da{" "}
            <a
              href="https://www.giorgiosforza.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d97706] hover:text-sacra-accent font-semibold transition-colors duration-300"
            >
              Giorgio Sforza
            </a>.
          </span>
        </div>

        {/* Back to Top Button */}
        <motion.button
          onClick={scrollToTop}
          whileHover={{ scale: 1.1 }}
          className="fixed bottom-8 right-8 w-12 h-12 bg-sacra-accent text-gray-900 rounded-full flex items-center justify-center shadow-lg hover:bg-amber-400 transition-colors z-50"
        >
          <FaArrowUp />
        </motion.button>
      </div>
    </footer>
  );
};

export default Footer;